import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Button, Table, Tag, Popover, DatePicker, message } from "antd";
import { FileTextOutlined, DollarOutlined } from "@ant-design/icons";
import { getAllCash, exportCash } from "../api/accounting";
import * as XLSX from "xlsx";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Shared/Utils/BankStatements.css"; // reusing the same CSS for consistent styling

const { RangePicker } = DatePicker;

const CashPage = forwardRef(({ filters, selectedBrand, onAffectCash }, ref) => {
  const [cashPayments, setCashPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Expose export function to parent component
  useImperativeHandle(ref, () => ({
    exportToExcel: async (returnFile = false) => {
      return await handleExportToExcel(returnFile);
    },
  }));

  useEffect(() => {
    if (!selectedBrand) return;
    const fetchCashPayments = async () => {
      setLoading(true);
      try {
        const response = await getAllCash({
          brand: selectedBrand.id,
          start_date: filters?.dateRange?.[0]?.format("YYYY-MM-DD"),
          end_date: filters?.dateRange?.[1]?.format("YYYY-MM-DD"),
          include_allocations: true,
        });

        console.log("Raw API response:", response);

        // Handle different response formats - new format returns { results, count, data }
        const cashResults =
          response?.results ||
          response?.data?.results ||
          (Array.isArray(response?.data) ? response.data : []);

        // Handle different response formats
        const processed = cashResults.map((cash) => {
          // Use the allocation status directly from the backend
          const allocatedAmount = parseFloat(cash.allocated_amount || 0);
          const percentAllocated = cash.percent_allocated || 0;

          // Map backend allocation status to frontend format
          const allocationStatus = {
            type: cash.allocation_status?.type || "unallocated",
            label: cash.allocation_status?.label || "Unallocated",
            color: cash.allocation_status?.color || "default",
          };

          return {
            ...cash,
            allocatedAmount,
            percentAllocated,
            allocationStatus,
            allocations: cash.cash_allocations || [],
          };
        });

        console.log(processed, "processed data in cash page");
        setCashPayments(processed);
      } catch (err) {
        console.error("Error fetching cash payments:", err);
        setCashPayments([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchCashPayments();
  }, [selectedBrand, filters]);

  const handleExportToExcel = async (returnFile = false) => {
    try {
      // Prepare export filters
      const exportFilters = {
        brand: selectedBrand?.id,
        start_date: filters?.dateRange?.[0]?.format("YYYY-MM-DD"),
        end_date: filters?.dateRange?.[1]?.format("YYYY-MM-DD"),
        include_allocations: true,
      };

      // Prepare export data (same logic for both paths)
      let exportData = [];

      if (cashPayments && cashPayments.length > 0) {
        exportData = cashPayments.map((cash, index) => ({
          "#": index + 1,
          "Cash #": cash.number || "-",
          Date: cash.date ? new Date(cash.date).toLocaleDateString() : "-",
          Type: cash.cash_type || "-",
          Partner: cash.partner?.name || "-",
          Amount: parseFloat(cash.total_amount || 0).toFixed(2),
          "Allocated Amount": (cash.allocatedAmount || 0).toFixed(2),
          "Percent Allocated": `${(cash.percentAllocated || 0).toFixed(1)}%`,
          "Allocation Status": cash.allocationStatus?.label || "Unknown",
          "Invoice Count": cash.allocations?.length || 0,
          "Invoice Numbers":
            cash.allocations
              ?.map((a) => a.invoice?.number || a.invoice_number)
              .filter(Boolean)
              .join(", ") || "No invoices",
        }));
      } else {
        // If no data, create at least headers with a sample row
        exportData = [
          {
            "#": "No data available",
            "Cash #": "-",
            Date: "-",
            Type: "-",
            Partner: "-",
            Amount: "-",
            "Allocated Amount": "-",
            "Percent Allocated": "-",
            "Allocation Status": "-",
            "Invoice Count": "-",
            "Invoice Numbers": "-",
          },
        ];
      }

      if (returnFile) {
        // For email functionality - create Excel file from current data
        const ws = XLSX.utils.json_to_sheet(exportData);

        // Set column widths for better formatting
        const columnWidths = [
          { wch: 5 }, // #
          { wch: 15 }, // Cash #
          { wch: 12 }, // Date
          { wch: 12 }, // Type
          { wch: 20 }, // Partner
          { wch: 12 }, // Amount
          { wch: 15 }, // Allocated Amount
          { wch: 15 }, // Percent Allocated
          { wch: 18 }, // Allocation Status
          { wch: 12 }, // Invoice Count
          { wch: 30 }, // Invoice Numbers
        ];
        ws["!cols"] = columnWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Cash Payments");

        // Generate buffer
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        return new File([blob], "Cash_Export.xlsx", {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      } else {
        // For direct download - try backend export API first
        try {
          const response = await exportCash(exportFilters);

          // Create blob and download
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `Cash_Export_${
            new Date().toISOString().split("T")[0]
          }.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          message.success("Cash export downloaded successfully!");
        } catch (apiError) {
          console.warn(
            "Backend export failed, using frontend export:",
            apiError
          );

          // Fallback to frontend export with the same exportData
          const ws = XLSX.utils.json_to_sheet(exportData);

          // Set column widths for better formatting
          const columnWidths = [
            { wch: 5 }, // #
            { wch: 15 }, // Cash #
            { wch: 12 }, // Date
            { wch: 12 }, // Type
            { wch: 20 }, // Partner
            { wch: 12 }, // Amount
            { wch: 15 }, // Allocated Amount
            { wch: 15 }, // Percent Allocated
            { wch: 18 }, // Allocation Status
            { wch: 12 }, // Invoice Count
            { wch: 30 }, // Invoice Numbers
          ];
          ws["!cols"] = columnWidths;

          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "Cash Payments");

          XLSX.writeFile(
            wb,
            `Cash_Export_${new Date().toISOString().split("T")[0]}.xlsx`
          );
          message.success("Cash export downloaded successfully!");
        }
      }
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export cash data");
      throw error;
    }
  };

  const makeFilters = (field) => {
    const values = cashPayments.map((c) => {
      const value = field.includes(".")
        ? field
            .split(".")
            .reduce(
              (obj, key) => (obj && obj[key] !== undefined ? obj[key] : "-"),
              c
            )
        : c[field];
      return value || "-";
    });
    return Array.from(new Set(values)).map((v) => ({ text: v, value: v }));
  };

  const dateFilterDropdown = ({ setSelectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }}>
      <RangePicker
        onChange={(dates, dateStrings) =>
          setSelectedKeys(dateStrings ? [dateStrings] : [])
        }
        style={{ marginBottom: 8, display: "block" }}
      />
      <Button
        type="primary"
        onClick={() => confirm()}
        size="small"
        style={{ width: 90, marginRight: 8 }}
      >
        Apply
      </Button>
      <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
        Reset
      </Button>
    </div>
  );

  const columns = [
    { title: "#", key: "index", render: (_, __, idx) => idx + 1, width: 50 },
    {
      title: "Cash #",
      dataIndex: "number",
      key: "number",
      filters: makeFilters("number"),
      onFilter: (v, r) => r.number === v,
      filterMultiple: false,
      filterSearch: true,
      width: 120,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (d) => (d ? new Date(d).toLocaleDateString() : "-"),
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      filterDropdown: dateFilterDropdown,
      filterIcon: (f) => (
        <FileTextOutlined style={{ color: f ? "#1890ff" : undefined }} />
      ),
      width: 150,
    },
    {
      title: "Type",
      dataIndex: "cash_type",
      key: "cash_type",
      filters: makeFilters("cash_type"),
      onFilter: (v, r) => r.cash_type === v,
      filterMultiple: false,
      filterSearch: true,
      width: 120,
    },
    {
      title: "Partner",
      dataIndex: "partner",
      key: "partner",
      render: (p) => p?.name || "-",
      filters: makeFilters("partner.name"),
      onFilter: (v, r) => r.partner?.name === v,
      filterMultiple: false,
      filterSearch: true,
      width: 200,
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (a) => parseFloat(a).toFixed(2),
      sorter: (a, b) => parseFloat(a.total_amount) - parseFloat(b.total_amount),
      width: 100,
    },
    {
      title: "Allocated",
      key: "allocated",
      render: (r) =>
        `${r.allocatedAmount.toFixed(2)} (${r.percentAllocated.toFixed(1)}%)`,
      sorter: (a, b) => a.percentAllocated - b.percentAllocated,
      width: 120,
    },
    {
      title: "Allocation Status",
      key: "allocationStatus",
      width: 160,
      render: (rec) => {
        const { type, label } = rec.allocationStatus;
        let style = {};
        if (type === "unallocated")
          style = { backgroundColor: "#f8d7da", color: "#721c24" };
        else if (type === "partially_allocated")
          style = { backgroundColor: "#fff3cd", color: "#856404" };
        else style = { backgroundColor: "#d4edda", color: "#155724" };
        return <Tag style={style}>{label}</Tag>;
      },
      filters: [
        { text: "Unallocated", value: "unallocated" },
        { text: "Partially Allocated", value: "partially_allocated" },
        { text: "Fully Allocated", value: "fully_allocated" },
      ],
      onFilter: (v, r) => v.includes(r.allocationStatus.type),
      filterMultiple: true,
    },
    {
      title: "Invoices",
      key: "invoices",
      width: 100,
      render: (rec) => {
        const allocations = rec.allocations || [];
        if (!allocations.length) return "No invoices";

        const uniqueInvoices = Array.from(
          new Set(allocations.map((a) => a.invoice?.number || a.invoice_number))
        ).length;

        const content = (
          <div style={{ width: 750 /* or whatever fits your design */ }}>
            <Table
              dataSource={allocations.map((item, idx) => ({
                ...item,
                tableKey: item.id || `allocation-${idx}`,
              }))}
              size="small"
              pagination={false}
              rowKey="tableKey"
              columns={[
                {
                  title: "Invoice #",
                  key: "invoice_number",
                  render: (_, record) =>
                    record.invoice?.number || record.invoice_number || "-",
                  width: 100,
                },
                {
                  title: "Amt",
                  dataIndex: "amount",
                  key: "amount",
                  render: (a) => parseFloat(a).toFixed(2),
                  width: 90,
                },
                {
                  title: "Date",
                  key: "date",
                  render: (_, record) => {
                    const date = record.date || record.allocation_date;
                    return date ? new Date(date).toLocaleDateString() : "-";
                  },
                  width: 100,
                },
              ]}
            />
          </div>
        );

        return (
          <Popover
            content={content}
            title={`${uniqueInvoices} Invoice${uniqueInvoices > 1 ? "s" : ""}`}
            trigger="click"
          >
            <Button size="small" icon={<FileTextOutlined />}>
              {uniqueInvoices}
            </Button>
          </Popover>
        );
      },
    },
    {
      title: "",
      key: "actions",
      fixed: "right",
      width: 140,
      render: (_, rec) => (
        <Button
          type="primary"
          size="small"
          icon={<DollarOutlined />}
          onClick={() => onAffectCash(rec.id)}
          disabled={rec.allocationStatus.type === "fully_allocated"}
        >
          Allocate Cash
        </Button>
      ),
    },
  ];

  return (
    <div className="table-responsive p-2">
      <Table
        rowClassName={(rec) =>
          rec.allocationStatus.type === "unallocated"
            ? "table-danger-outline"
            : rec.allocationStatus.type === "partially_allocated"
            ? "table-warning-outline"
            : "table-success-outline"
        }
        columns={columns}
        dataSource={cashPayments}
        rowKey="id"
        pagination={{ pageSize: 20 }}
        loading={loading}
        scroll={{ x: "max-content" }}
        size="small"
      />
    </div>
  );
});
CashPage.displayName = "CashPage";

export default CashPage;
